import type { ResponseReadAll } from "epsm-api-types";
import type { MatrixQuarterlyReport } from "epsm-api-types/models";
import type { PriceInfo } from "epsm-api-types/ws";
import type { IChartApi, ISeriesApi, UTCTimestamp } from "lightweight-charts";
import type { RefObject } from "react";
import { shiftDateToLocalTimeZone } from "@/lib/utils";
import ErrorHandler from "@/services/ErrorService/ErrorService";
import { chartColors } from "../../const";
import { TIMEFRAME_MAP } from "../../services/PriceService/PriceService";
import type { Options } from "../../services/UseData";
import type { Timeframes } from "../../types";
import { setFullVisibleLogicalRange } from "../setFullVisibleLogicalRange";
import { updateVolumeData } from "../updateVolumeData";

type Args = {
	ticker: string;
	volumeSeries: ISeriesApi<"Histogram"> | null;
	mainSeries: ISeriesApi<"Candlestick"> | null;
	chart: IChartApi | null;
	duration: { current: number };
	from: number;
	execute: (
		options: Readonly<Options>,
	) => Promise<ResponseReadAll<PriceInfo<UTCTimestamp>> | null>;
	executeQuarterly: (
		options: Readonly<Options>,
	) => Promise<ResponseReadAll<MatrixQuarterlyReport> | null>;
	timeFrame: Timeframes;
	setQuarterlyReports: (reports: MatrixQuarterlyReport[]) => void;
	quarterlyReportsRef: React.RefObject<MatrixQuarterlyReport[]>;
	pricesRef: React.RefObject<PriceInfo<UTCTimestamp>[]>;
	setHasData: (hasData: boolean) => void;
	showFullRange: boolean;
	isFullRangeSetRef: RefObject<boolean>;
};

export function fetchDataForChart(args: Args) {
	const {
		ticker,
		volumeSeries,
		mainSeries,
		chart,
		duration,
		from,
		timeFrame,
		quarterlyReportsRef,
		pricesRef,
		showFullRange,
		isFullRangeSetRef,
		setQuarterlyReports,
		setHasData,
		execute,
		executeQuarterly,
	} = args;

	if (!ticker || !volumeSeries || !mainSeries || !chart || !duration.current) {
		return;
	}

	const fetchQuarterlyReports = executeQuarterly({
		params: {
			sort: [{ name: "earning_date", order: 1 }],
			filter: [
				{ name: "ticker", condition: "eq", value: ticker },
				{ name: "earning_date", condition: "gte", value: from },
				{
					name: "earning_date",
					condition: "lt",
					value: from + duration.current,
				},
			],
		},
	});

	const fetchPrices = execute({
		timeFrame,
		params: {
			filter: [
				{ name: "ticker", condition: "eq", value: ticker },
				{
					name: "date",
					condition: "gte",
					value: from,
				},
				{
					name: "date",
					condition: "lt",
					value: from + duration.current,
				},
			],
			sort: [{ name: "date", order: 1 }],
		},
	});

	Promise.allSettled([fetchQuarterlyReports, fetchPrices])
		.then(([quarterlyResult, pricesResult]) => {
			if (quarterlyResult.status === "fulfilled") {
				const newReports = quarterlyResult.value?.data ?? [];
				const combinedReports = [
					...newReports,
					...quarterlyReportsRef.current,
				].filter((report) => !!report.earning_date);

				quarterlyReportsRef.current = combinedReports;
				setQuarterlyReports(combinedReports);
			} else {
				ErrorHandler.logError(quarterlyResult.reason);
			}

			if (pricesResult.status === "fulfilled") {
				const data =
					pricesResult.value?.data?.map((item: PriceInfo<UTCTimestamp>) => {
						const time = (
							TIMEFRAME_MAP[timeFrame].isIntraday
								? Math.trunc(shiftDateToLocalTimeZone(item.time) / 1000)
								: item.time / 1000
						) as UTCTimestamp;
						return {
							...item,
							time: time,
						};
					}) ?? [];

				const uniqueByTime = new Map<number, PriceInfo<UTCTimestamp>>();
				for (const d of [...pricesRef.current, ...data]) {
					uniqueByTime.set(d.time as number, d);
				}

				const result = Array.from(uniqueByTime.values()).sort(
					(a, b) => a.time - b.time,
				);

				const updatedVolumeData = updateVolumeData(
					result,
					chartColors.volumesUp,
					chartColors.volumesDown,
				);

				volumeSeries.setData(updatedVolumeData);
				mainSeries.setData(result);
				pricesRef.current = result;
				setHasData(!!data.length);

				if (showFullRange && !isFullRangeSetRef.current) {
					setFullVisibleLogicalRange({
						chart,
						result,
						timeFrame,
					});
					isFullRangeSetRef.current = true;
				}
			} else {
				ErrorHandler.logError(pricesResult.reason);
			}
		})
		.catch((e) => {
			ErrorHandler.logError(e);
		});
}
